<script setup lang="ts">
  import { computed } from 'vue';

  const props = defineProps<{
    value: number;
    periods: number[];
    annualDiscountPercent?: number | null;
  }>();

  const emit = defineEmits<{
    (e: 'input', value: number): void;
  }>();

  const safePeriods = computed(() => {
    return (props.periods ?? []).filter((p) => p === 1 || p === 12);
  });

  const annualDiscountLabel = computed(() => {
    const raw = props.annualDiscountPercent;
    if (typeof raw !== 'number' || Number.isNaN(raw) || raw <= 0) return null;
    return `Ahorrá ${Math.round(raw)}%`;
  });

  function onChange(val: number) {
    emit('input', val);
  }

  function getLabel(months: number) {
    if (months === 1) return '1 mes';
    if (months === 12) return '12 meses';
    return `${months} meses`;
  }
</script>

<template>
  <div class="billing-period-toggle">
    <v-btn-toggle
      class="billing-period-toggle__toggle"
      :value="value"
      mandatory
      @change="onChange"
    >
      <v-btn
        v-for="p in safePeriods"
        :key="p"
        :value="p"
        class="billing-period-toggle__btn"
      >
        <span class="billing-period-toggle__btn-content">
          <span class="billing-period-toggle__btn-label">{{ getLabel(p) }}</span>

          <span
            v-if="p === 12 && annualDiscountLabel"
            class="billing-period-toggle__btn-badge"
          >
            {{ annualDiscountLabel }}
          </span>
        </span>
      </v-btn>
    </v-btn-toggle>
  </div>
</template>

<style scoped lang="scss">
  .billing-period-toggle {
    display: flex;
    justify-content: center;
    width: 100%;

    &__toggle {
      border-radius: 999px;
      padding: 4px;
      background: #f6f7fb;
      border: 1px solid rgba(15, 23, 42, 0.08);
      box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);

      :deep(.v-btn) {
        border-radius: 999px;
        text-transform: none;
        letter-spacing: 0;
        font-weight: 700;
        min-width: 132px;
        padding: 10px 14px;
        line-height: 1.1;
        color: rgba(15, 23, 42, 0.82);
        transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.2s ease;

        &:hover {
          transform: translateY(-1px);
        }
      }

      :deep(.v-btn--active) {
        background: linear-gradient(135deg, #ff6f00, #ff9800);
        color: #fff !important;
        box-shadow: 0 12px 26px rgba(255, 111, 0, 0.25);

        .billing-period-toggle__btn-badge {
          background: rgba(255, 255, 255, 0.18);
          color: #fff;
        }
      }
    }

    &__btn-content {
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
    }

    &__btn-label {
      font-size: 14px;
    }

    &__btn-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 2px 10px;
      border-radius: 999px;
      background: rgba(255, 111, 0, 0.12);
      color: rgba(120, 53, 15, 0.98);
      font-size: 12px;
      font-weight: 800;
      letter-spacing: 0.2px;
      white-space: nowrap;
    }
  }
</style>

